import type { GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { NextSeo, WebPageJsonLd } from 'next-seo'
import dynamic from 'next/dynamic'
import Script from 'next/script'
import React from 'react'
import SearchManager from 'src/components/SearchManager'
import FilterProvider from 'src/context/FilterProvider'
import { getCanonicalUrl } from 'src/utils/seo'

const LostListingToast = dynamic(
  () => import('src/components/LostListingToast'),
  { ssr: false }
)
const GooglePopup = dynamic(() => import('src/components/Login/GooglePopup'), {
  ssr: false
})
const WhatsAppButton = dynamic(() => import('src/components/WhatsappButton'))

const title = 'Renta mini-bodegas, estacionamientos, bodegas y naves | SpotMe'
const description =
  'Ahorra hasta 30% en la renta de espacios personales o de negocio. Encuentra bodegas en renta cerca de donde las necesitas. Renta estacionamiento para tu auto o flotilla.'
const image = 'https://www.spotme.mx/images/cover.png'

type IProps = {
  buildDate: string
  locale: string
}

const Page: NextPage<IProps> = ({ buildDate, locale }) => {
  const { t } = useTranslation('common')
  const url = getCanonicalUrl('/search', locale)

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={url}
        openGraph={{
          type: 'website',
          url,
          title,
          description,
          locale: locale === 'en' ? 'en_US' : 'es_ES',
          images: [{ url: image, width: 1200, height: 630 }]
        }}
        // noindex BOTH locales: /search is infinite query-param space and a
        // thin duplicate of the pillar/city/hood SSG pages (the real SEO
        // surfaces). ES was index,follow until 2026-06-11, which let Google
        // index malformed legacy URLs (?city=guadalajara- etc.) that kept
        // pulling organic traffic into broken searches.
        //
        // The PROP, never an `additionalMetaTags` robots entry: next-seo
        // always emits its own keyed `robots` tag (`index,follow` when the
        // prop is absent), so the hand-written one shipped BOTH `index` and
        // `noindex` here — the contradictory pair Ahrefs flagged 2026-08-28.
        noindex
        additionalMetaTags={[
          {
            name: 'keywords',
            content:
              'renta mini bodegas, renta estacionamientos, renta bodegas, renta naves industriales, almacenamiento mexico, espacios en renta'
          }
        ]}
      />
      <WebPageJsonLd
        description={description}
        id={url + '#webpage'}
        lastReviewed={buildDate}
        reviewedBy={{ '@type': 'Organization', name: 'SpotMe' }}
      />
      <Script
        id="search-action-jsonld"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            url: 'https://www.spotme.mx',
            name: 'SpotMe',
            potentialAction: {
              '@type': 'SearchAction',
              target: {
                '@type': 'EntryPoint',
                urlTemplate:
                  'https://www.spotme.mx/search?description={search_term_string}'
              },
              'query-input': 'required name=search_term_string'
            }
          })
        }}
      />

      <main className="h-full">
        <LostListingToast />
        <FilterProvider>
          <SearchManager
            header={<h1 className="sr-only">{t('SEARCH_BAR.PAGE_H1')}</h1>}
          />
        </FilterProvider>

        <GooglePopup />
        <WhatsAppButton msg={t('WHATSAPP_SEARCH_MSG')} />
      </main>
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const translations = await serverSideTranslations(locale ?? 'es', [
    'common',
    'footer',
    'nav',
    'host_cta',
    'platform_stats',
    'auth',
    // SearchManager mounts OnDemandSearchCard (mid-grid) and EmptyState →
    // OnDemandRecoveryCard, both of which call useTranslation('page_mini_bodega')
    // for the CROSS_LINK_ON_DEMAND keys. Without this, the SelfStorage on-demand
    // recovery + cross-link cards render raw keys on /search?city=monterrey.
    'page_mini_bodega'
  ])
  return {
    props: {
      ...translations,
      buildDate: new Date().toISOString().split('T')[0],
      locale: locale ?? 'es'
    }
  }
}

export default React.memo(Page)
